import React, { memo, useMemo } from "react";
import _ from "lodash";
import { useQuery } from "@apollo/client";
import { getFormattedUserName } from "Utils/GetFormattedUserName";
import {
  getTranscriptEntityStudentsQuery,
  getYearGroupStudentsQuery,
} from "TranscriptsV3/modules/TranscriptsV3Queries";
import { ASSOCIATION_ENTITY_COLLECTION_KEYS } from "TranscriptsV3/components/GenericCustomFieldModal/modules/constants";
import StudentListContent from "../StudentsList/StudentListContent";

export const YEAR_GROUP_ENTITY_KEY = "YEAR_GROUP";

const ENTITY_TYPE_BY_COLLECTION_KEY = {
  [ASSOCIATION_ENTITY_COLLECTION_KEYS.TRANSCRIPT_COURSES]: "TRANSCRIPT_COURSE",
  [ASSOCIATION_ENTITY_COLLECTION_KEYS.TRANSCRIPT_GRADE_SCALES]:
    "TRANSCRIPT_GRADE_SCALE",
  [ASSOCIATION_ENTITY_COLLECTION_KEYS.TRANSCRIPT_GRADES]: "TRANSCRIPT_GRADE",
  [ASSOCIATION_ENTITY_COLLECTION_KEYS.TRANSCRIPT_SCHOOLS]: "TRANSCRIPT_SCHOOL",
  [ASSOCIATION_ENTITY_COLLECTION_KEYS.TRANSCRIPT_ACADEMIC_YEARS]:
    "TRANSCRIPT_ACADEMIC_YEAR",
  [ASSOCIATION_ENTITY_COLLECTION_KEYS.TRANSCRIPT_GRADING_PERIODS]:
    "TRANSCRIPT_GRADING_PERIOD",
  [ASSOCIATION_ENTITY_COLLECTION_KEYS.TRANSCRIPT_LEVELS]: "TRANSCRIPT_LEVEL",
};

const TranscriptStudentsList = ({
  count,
  entityCollectionKey,
  entityId,
  selectedAcademicYearId,
}) => {
  const isYearGroup = entityCollectionKey === YEAR_GROUP_ENTITY_KEY;
  const entityType = ENTITY_TYPE_BY_COLLECTION_KEY[entityCollectionKey];

  const { data, networkStatus } = useQuery(
    isYearGroup ? getYearGroupStudentsQuery : getTranscriptEntityStudentsQuery,
    {
      fetchPolicy: "cache-and-network",
      skip: isYearGroup
        ? !entityId || !selectedAcademicYearId
        : !entityId || !entityType,
      variables: isYearGroup
        ? {
            yearGroupId: entityId,
            studentFilters: {
              archivalState: "ALL",
              academicYearIds: [selectedAcademicYearId],
              orderBy: "FULL_NAME",
              orderByDirection: "ASC",
            },
          }
        : { id: entityId, type: entityType },
    }
  );

  const students = useMemo(() => {
    return _.chain(data)
      .get(["node", isYearGroup ? "students" : "associations", "edges"])
      .map(edge => {
        const student = _.get(edge, isYearGroup ? "node" : "entity", {});

        return {
          ...student,
          name: getFormattedUserName({ user: student, type: "student" }),
        };
      })
      .value();
  }, [data, isYearGroup]);

  return (
    <StudentListContent
      count={count}
      isData={networkStatus === 7}
      isLoading={networkStatus === 1}
      students={students}
    />
  );
};

TranscriptStudentsList.displayName = "TranscriptStudentsList";

export default memo(TranscriptStudentsList);
